import { useMemo } from 'react';
import { type IconLibrary } from 'src/components/DynamicIcon/dynamic-icon';
import {
  Address,
  Project,
  ProjectStages,
  useDeleteProject,
  useGroupRules,
  UserGroupRules,
} from 'src/graphql';
import { EntityNames } from 'src/graphql/types';
import { Modals, useShowModals } from 'src/store/modals';
import { useConfirmationModal, useCopyToClipboard, useEntityFullUrl } from 'src/utils/hooks';

const LUCIDE_ICON_LIBRARY: IconLibrary = 'lucide';

export interface ProjectAction {
  label: string;
  icon: string;
  iconLibrary: IconLibrary;
  testId?: string;
  isAllowed?: boolean;
  handler: () => void | Promise<void>;
}

type ProjectActionKey =
  | 'submitDay1'
  | 'proposalWon'
  | 'proposalLost'
  | 'copyProjectId'
  | 'copyProjectUrl'
  | 'deleteProject';

export const useProjectActions = ({
  project,
  address,
}: {
  project: Project;
  address: Address;
}) => {
  const { confirm } = useConfirmationModal();
  const { copyToClipboard } = useCopyToClipboard();
  const { deleteProject } = useDeleteProject();
  const [openSubmitDay1Modal, openProposalWonModal, openProposalLostModal] = useShowModals(
    Modals.SubmitDay1,
    Modals.ProposalWon,
    Modals.ProposalLost,
  );

  const { groupRules } = useGroupRules();

  const canChangeProjectStatus =
    Boolean(groupRules[UserGroupRules.AccessToChangeProjectStatus]) && !project.isDeleted;

  const projectFullUrl = useEntityFullUrl(project.partnerId, EntityNames.Project, project.id);

  return useMemo(
    (): Record<ProjectActionKey, ProjectAction> => ({
      submitDay1: {
        label: 'Submit Day1',
        icon: 'calendar-check',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        testId: 'project-card-submit-day1-action',
        isAllowed:
          project.projectStageId === ProjectStages.ActiveProject && canChangeProjectStatus,
        handler: () => openSubmitDay1Modal({ address, project }),
      },
      proposalWon: {
        label: 'Proposal won',
        icon: 'circle-check',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        testId: 'project-card-proposal-won-action',
        isAllowed: project.projectStageId === ProjectStages.Proposal && canChangeProjectStatus,
        handler: () => openProposalWonModal({ address, project }),
      },
      proposalLost: {
        label: 'Proposal lost',
        testId: 'project-card-proposal-lost-action',
        icon: 'circle-x',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        isAllowed:
          ![ProjectStages.ServiceClient, ProjectStages.ProposalLost].includes(
            project.projectStageId,
          ) && canChangeProjectStatus,
        handler: () => openProposalLostModal({ address, project }),
      },
      copyProjectId: {
        label: 'Copy Project ID',
        icon: 'copy',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        handler: async () => {
          await copyToClipboard(project.id);
        },
      },
      copyProjectUrl: {
        label: 'Copy Project URL',
        icon: 'link',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        isAllowed: !!projectFullUrl,
        handler: async () => {
          await copyToClipboard(projectFullUrl);
        },
      },
      deleteProject: {
        label: 'Delete',
        icon: 'trash-2',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        isAllowed: !project.isDeleted,
        handler: async () => {
          const confirmed = await confirm({
            title: 'Delete project?',
            body: 'The project will be removed from this site.',
            confirmLabel: 'Delete project',
            danger: true,
          });
          if (confirmed) {
            const { errors } = await deleteProject(project.id);
            if (errors) {
              console.error(errors);
              return;
            }
          }
        },
      },
    }),
    [
      address,
      confirm,
      copyToClipboard,
      deleteProject,
      canChangeProjectStatus,
      openProposalLostModal,
      openProposalWonModal,
      openSubmitDay1Modal,
      project,
      projectFullUrl,
    ],
  );
};
